"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import type { SignInOutput } from "aws-amplify/auth";
import { useTranslations } from "next-intl";
import { useEffect, useId, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
	Box,
	Button,
	Checkbox,
	type DialogProps,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
    useTheme
} from "@/components/controls/base-controls";
import {
	MdOutlineVisibility,
	MdOutlineVisibilityOff,
} from "@/components/controls/icons";
import MfaDialog from "@/components/dialogs/mfa.dialog";

import {
	type CredentialsData,
	credentialsSchema,
} from "@/core/schemas/sign-in.schema";
import {
	resendUserConfirmation,
	setSignInMfaPreference,
	userSignIn,
} from "@/core/services/user.service";
import { useSignInStore } from "@/core/stores/sign-in.store";
import type { Credentials } from "@/core/types/credentials.type";
import { MFA, type MfaType } from "@/core/types/mfa.type";
import { Link, useRouter } from "@/i18n/navigation";

const CredentialsPage = () => {
	const t = useTranslations();

	const theme = useTheme();
    // Returns true on mobile (xs, sm), false on desktop (md, lg, xl)
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const email_id = useId();
	const password_id = useId();

	const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
	const [mfaDialogScroll, setMfaDialogScroll] =
		useState<DialogProps["scroll"]>("paper");
	const [mfaPreference, setMfaPreference] = useState<MfaType | undefined>(
		undefined,
	);

	const [errorMessage, setErrorMessage] = useState("");
	const [showPassword, setShowPassword] = useState(false);

	const {
		resetSignInData,
		setStep,
		setTotpSetupUri,
		signInData,
		updateSignInData,
	} = useSignInStore();

	const router = useRouter();

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
	} = useForm<CredentialsData>({
		resolver: zodResolver(credentialsSchema),
		defaultValues: {
			email: "",
			password: "",
			rememberMe: false,
		},
		mode: "onChange",
	});

	useEffect(() => {
		const savedEmail = localStorage.getItem("rememberedEmail");
		if (savedEmail) {
			setValue("email", savedEmail);
			setValue("rememberMe", true);
		}
	}, [setValue]);

	const handleRegisterClick = () => {
		reset();
		resetSignInData();
		router.push("/reg");
	};

	const submitCredentials = useMutation({
		mutationFn: async (data: Credentials) => {
			return await userSignIn(data);
		},
		onSuccess: async (results: SignInOutput) => {
			setErrorMessage("");
			if (results.isSignedIn) {
				router.push("/pro/deals");
			} else if (results.nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION") {
				handleMfaDialogOpen("paper")();
			} else if (
				results.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE" ||
				results.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
				results.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
			) {
				console.log("Confirm with code.");
				setStep("verification");
				router.push("/sig/verify");
			} else {
				setErrorMessage(`${t("sign_in.invalid")}`);
			}
		},
		onError: (error, variables) => {
			console.log("Credentials check: ", error.name, error.message);
			if (error.name === "UserNotConfirmedException") {
				resendUserConfirmation({ username: variables.email });
				setStep("confirmation");
				router.push("/sig/conf");
			} else {
				setErrorMessage(`${t("sign_in.invalid")}`);
			}
		},
		onSettled: (data, error) => {
			console.log("Mutation settled (finished):", data || error);
		},
	});

	const onSubmit = (data: CredentialsData) => {
		const { rememberMe, ...rest } = data;

		if (rememberMe) {
			localStorage.setItem("rememberedEmail", rest.email);
		} else {
			localStorage.removeItem("rememberedEmail");
		}

		updateSignInData(rest);

		submitCredentials.mutate(rest);
	};

	// --- MFA setup selection dialog handlers ---

	const handleMfaDialogOpen = (scrollType: DialogProps["scroll"]) => () => {
		setMfaDialogOpen(true);
		setMfaDialogScroll(scrollType);
	};

	const handleMfaDialogCancel = () => {
		setMfaDialogOpen(false);
	};

	const handleMfaDialogSubmit = async (value: MfaType) => {
		setMfaPreference(value);
		setMfaDialogOpen(false);

		if (value === MFA.TOTP) {
			await handleTotpSetup();
		} else {
			await handleEmailOrSmsSetup(value);
		}
	};

	const handleTotpSetup = async () => {
		try {
			const { nextStep } = await setSignInMfaPreference(MFA.TOTP);
			if (nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP" && nextStep.totpSetupDetails) {
				const { getSetupUri } = nextStep.totpSetupDetails;
				const setupUri = getSetupUri("Navairra", signInData.email ?? "").toString();
				setTotpSetupUri(setupUri);
				setStep("mfa");
				router.push("/sig/mfa?qr=1");
			} else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
				setStep("verification");
				router.push("/sig/verify");
			}
		} catch (error) {
			console.error("TOTP setup error:", error);
			setErrorMessage(`${t("errors.processingError")}`);
		}
	};

	const handleEmailOrSmsSetup = async (mfa: MfaType) => {
		try {
			const { nextStep } = await setSignInMfaPreference(mfa);
			if (
				nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE" ||
				nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE"
			) {
				setStep("verification");
				router.push("/sig/verify");
			}
		} catch (error) {
			console.error("MFA setup error:", error);
			setErrorMessage(`${t("errors.processingError")}`);
		}
	};

	return (
		<>
			<Typography variant="h3" sx={{ marginBottom: "1.5em" }}>
				{t("sign_in.step1")}
			</Typography>

			<Box
				component="form"
				noValidate
				onSubmit={handleSubmit(onSubmit)}
				sx={{
					display: "flex",
					flexDirection: "column",
					rowGap: 2,
					pt: "1em",
					width: "100%",
					maxWidth: "400px",
				}}
			>
				<Controller
					name="email"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							fullWidth
							required
							id={email_id}
							label={t("email.label")}
							placeholder={t("email.label")}
							autoComplete="username"
							error={!!errors.email}
							helperText={
								errors.email?.message && t(errors.email.message as string)
							}
							variant="outlined"
							inputMode="email"
							type="email"
							slotProps={{
								inputLabel: { shrink: isMobile ? true : undefined }
							}}
						/>
					)}
				/>

				<Controller
					name="password"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							fullWidth
							required
							id={password_id}
							label={t("password.label")}
							placeholder={t("password.label")}
							autoComplete="current-password"
							error={!!errors.password}
							helperText={
								errors.password?.message && t(errors.password.message as string)
							}
							type={showPassword ? "text" : "password"}
							variant="outlined"
							slotProps={{
								htmlInput: { maxLength: 24 },
								inputLabel: { shrink: isMobile ? true : undefined },
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label={
													showPassword
														? "Hide the password"
														: "Display the password"
												}
												onClick={() => setShowPassword(!showPassword)}
												onMouseDown={(e) => e.preventDefault()}
												edge="end"
											>
												{showPassword ? (
													<MdOutlineVisibilityOff />
												) : (
													<MdOutlineVisibility />
												)}
											</IconButton>
										</InputAdornment>
									),
								},
							}}
						/>
					)}
				/>

				<Box sx={{ textAlign: "right" }}>
					<Link
						href="/sig/pw-reset"
						style={{
							color: "var(--mui-palette-text-secondary)",
							fontSize: "0.875rem",
							textDecoration: "none",
						}}
					>
						{t("pw_reset.forgotPassword")}
					</Link>
				</Box>

				<FormControlLabel
					label={t("rememberMe.label")}
					sx={{
						margin: "0",
					}}
					control={
						<Controller
							name="rememberMe"
							control={control}
							render={({ field }) => (
								<Checkbox
									{...field}
									checked={field.value}
								/>
							)}
						/>
					}
				/>

				{errorMessage && (
					<Typography variant="error">{errorMessage}</Typography>
				)}

				<Button
					variant="contained"
					color="secondary"
					sx={{
						margin: "2em 0 .5em",
						textTransform: "none",
						width: "100%",
					}}
					type="submit"
					disabled={submitCredentials.isPending}
				>
					{t("sign_in.submit")}
				</Button>
			</Box>

			<MfaDialog
				mfaPreference={mfaPreference}
				open={mfaDialogOpen}
				onClose={handleMfaDialogCancel}
				onMfaSubmit={handleMfaDialogSubmit}
				scroll={mfaDialogScroll}
			/>

			<Stack
				sx={{
					rowGap: 2,
					width: "100%",
					maxWidth: "400px",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
					}}
				>
					<Box sx={{ fontSize: "24px" }}>&#x291B;</Box>
					<Box sx={{ fontSize: "14px", position: "relative", top: "2px" }}>
						&nbsp;&nbsp;{t("misc.or")}&nbsp;&nbsp;
					</Box>
					<Box sx={{ fontSize: "24px" }}>&#x291C;</Box>
				</Box>

				<Button variant="square" type="button" onClick={handleRegisterClick}>
					{t("sign_in.registerLink")}
				</Button>
			</Stack>
		</>
	);
};

export default CredentialsPage;
